import React, { useState, useEffect } from 'react';
import { X, FileText, DollarSign, CheckCircle, AlertCircle, ExternalLink, Eye, Edit, Printer, Trash2, Send, Clock, Check, AlertTriangle, Shield, Ban, RotateCcw, CheckCircle2 } from 'lucide-react';
import { apiFetch } from '../services/api';
import './ClientPaymentsModal.css';

// Status da PARCELA CPFL (manual seção 4.7) — é isso que diz de verdade se
// o cliente pagou (5 = Arrecadado). Não usar a numeração de PRODUTO (4.13).
const CPFL_STATUS_MAP = {
  1: { text: 'Cancelada',  icon: Ban,           color: '#e11d48' },
  2: { text: 'Aguardando', icon: Clock,         color: '#d97706' },
  3: { text: 'Irregular',  icon: AlertTriangle, color: '#e11d48' },
  4: { text: 'Faturado',   icon: FileText,      color: '#d97706' },
  5: { text: '✅ PAGO',    icon: CheckCircle2,  color: '#059669' },
  6: { text: 'Estornado',  icon: RotateCcw,     color: '#64748b' },
};

export default function ClientPaymentsModal({ client, onClose }) {
  const isCpfl = client._source === 'cpfl';
  const [payments, setPayments] = useState([]);
  const [cpflCharges, setCpflCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [anexosPorCharge, setAnexosPorCharge] = useState({});
  const [buscandoAnexos, setBuscandoAnexos] = useState({});

  const buscarAnexosReais = async (chargeId) => {
    setBuscandoAnexos((prev) => ({ ...prev, [chargeId]: true }));
    try {
      const r = await apiFetch(`/api/cpfl/anexos/${chargeId}`);
      setAnexosPorCharge((prev) => ({ ...prev, [chargeId]: r.arquivos || [] }));
    } catch (err) {
      setAnexosPorCharge((prev) => ({ ...prev, [chargeId]: [] }));
    } finally {
      setBuscandoAnexos((prev) => ({ ...prev, [chargeId]: false }));
    }
  };

  useEffect(() => {
    if (isCpfl) {
      fetchCpflCharges();
    } else {
      fetchPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const fetchCpflCharges = async () => {
    setLoading(true);
    setError(null);
    try {
      const cpf = (client.cpf || '').replace(/\D/g, '');
      const response = await apiFetch(`/api/cpfl/cobrancas?search=${cpf}&limit=50`);
      setCpflCharges(response.data || []);
    } catch (err) {
      console.error('Erro ao buscar cobranças CPFL:', err);
      setError('Não foi possível carregar as cobranças');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    setLoading(true);
    setError(null);
    try {
      const customerId = client.asaas_customer_id || client.id;
      
      console.log('🔍 Buscando pagamentos do cliente:', customerId);
      
      const response = await apiFetch(`/api/asaas/local-payments?asaas_customer_id=${customerId}`);
      
      console.log('✅ Pagamentos encontrados:', response.data?.length || 0);
      setPayments(response.data || []);
    } catch (err) {
      console.error('Erro ao buscar pagamentos:', err);
      setError('Não foi possível carregar as cobranças');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // ── Parcelas da CPFL: o vencimento vem como texto M/D/AAAA (padrão dos EUA); aqui mostramos DD/MM/AAAA ──
  const dataBRParcela = (v) => {
    const s = String(v == null ? '' : v).trim();
    if (!s) return '';
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (m) {
      const p2 = (x) => String(x).padStart(2, '0');
      return Number(m[1]) > 12 ? `${p2(m[1])}/${p2(m[2])}/${m[3]}` : `${p2(m[2])}/${p2(m[1])}/${m[3]}`;
    }
    return s;
  };
  const chaveMesParcela = (p) => {
    const m = String((p && p.mesReferencia) || '').match(/^(\d{1,2})\/(\d{4})$/);
    return m ? Number(m[2]) * 100 + Number(m[1]) : Infinity;
  };
  const ordenarParcelas = (lista) => [...lista].sort((a, b) => {
    const ka = chaveMesParcela(a);
    const kb = chaveMesParcela(b);
    if (ka === kb) return 0;
    if (ka === Infinity) return 1;
    if (kb === Infinity) return -1;
    return kb - ka; // mês mais novo em cima, mais antigo embaixo
  });
  const parcelasDaCobranca = (c) => (Array.isArray(c.detalheParcelas) ? c.detalheParcelas : []);
  const statusParcela = (p) => String((p && p.status) || '').toLowerCase();

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const getBillingTypeText = (type) => {
    const types = {
      'BOLETO': 'Boleto Bancário',
      'CREDIT_CARD': 'Cartão de Crédito',
      'PIX': 'PIX',
      'DEBIT_CARD': 'Cartão de Débito',
    };
    return types[type] || type || '—';
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      'RECEIVED': { text: 'Recebido', icon: Check, class: 'received', color: '#059669' },
      'PENDING': { text: 'Aguardando pagamento', icon: Clock, class: 'pending', color: '#d97706' },
      'OVERDUE': { text: 'Vencido', icon: AlertTriangle, class: 'overdue', color: '#e11d48' },
      'CONFIRMED': { text: 'Confirmado', icon: Shield, class: 'confirmed', color: '#2563eb' },
    };
    return statusMap[status] || { text: status, icon: AlertCircle, class: 'default', color: '#64748b' };
  };

  const handleConfirmPayment = async (payment) => {
    if (window.confirm(`Confirmar recebimento da cobrança de ${formatCurrency(payment.value)}?`)) {
      try {
        const response = await apiFetch(`/api/asaas/payments/${payment.id}/receive`, {
          method: 'POST',
          body: JSON.stringify({
            paymentDate: new Date().toISOString().split('T')[0],
            value: payment.value
          })
        });
        alert('Pagamento confirmado com sucesso!');
        await fetchPayments();
      } catch (err) {
        alert('Erro ao confirmar pagamento: ' + err.message);
      }
    }
  };

  const handleEditPayment = (payment) => {
    alert(`Editar cobrança ${payment.id}\n\nEm breve: formulário de edição`);
  };

  const handleSendReminder = (payment) => {
    const clientName = payment.customerName || client.full_name || client.name;
    const message = `Olá ${clientName},\n\nLembramos que sua cobrança de ${formatCurrency(payment.value)} com vencimento em ${formatDate(payment.dueDate)} está ${payment.status === 'PENDING' ? 'aguardando pagamento' : payment.status === 'OVERDUE' ? 'vencida' : 'pendente'}.\n\nPor favor, realize o pagamento o mais breve possível.\n\nObrigado!`;
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
  };

  const handleDeletePayment = async (payment) => {
    if (window.confirm(`Tem certeza que deseja remover a cobrança de ${formatCurrency(payment.value)}?\n\nEsta ação não pode ser desfeita.`)) {
      try {
        await apiFetch(`/api/asaas/payments/${payment.id}`, {
          method: 'DELETE'
        });
        alert('Cobrança removida com sucesso!');
        await fetchPayments();
      } catch (err) {
        alert('Erro ao remover cobrança: ' + err.message);
      }
    }
  };

  // ── Cálculos de resumo (Asaas) ──
  const totalValue = payments.reduce((sum, p) => sum + (p.value || 0), 0);
  const receivedValue = payments.filter(p => p.status === 'RECEIVED' || p.status === 'CONFIRMED').reduce((sum, p) => sum + (p.value || 0), 0);
  const pendingValue = payments.filter(p => p.status === 'PENDING' || p.status === 'OVERDUE').reduce((sum, p) => sum + (p.value || 0), 0);

  const sortedPayments = [...payments].sort((a, b) => new Date(b.dueDate || 0) - new Date(a.dueDate || 0));
  const displayedPayments = showAll ? sortedPayments : sortedPayments.slice(0, 5);

  // ── Cálculos de resumo (CPFL) ──
  // Resumo CPFL: somado pelas PARCELAS (o que a tabela mostra). Cobrança sem detalhe de parcela usa a situação da cobrança, como antes.
  const somaCpfl = cpflCharges.reduce((acc, c) => {
    const ps = parcelasDaCobranca(c);
    if (ps.length === 0) {
      const v = c.valor || 0;
      acc.total += v;
      if (c.status === 5) acc.finalizadas += v;
      if (c.status === 2 || c.status === 3) acc.aguardando += v;
      return acc;
    }
    ps.forEach((p) => {
      const st = statusParcela(p);
      if (st.startsWith('cancel')) return;
      const v = parseFloat(p.valor) || c.valor || 0;
      acc.total += v;
      if (st.startsWith('arrecad')) acc.finalizadas += v;
      else if (st.startsWith('faturad') || st.startsWith('irregular')) acc.aguardando += v;
    });
    return acc;
  }, { total: 0, finalizadas: 0, aguardando: 0 });
  const cpflTotalValue = somaCpfl.total;
  const cpflFinalizadaValue = somaCpfl.finalizadas;
  const cpflAguardandoValue = somaCpfl.aguardando;
  const cpflQtdParcelas = cpflCharges.reduce((n, c) => n + parcelasDaCobranca(c).filter((p) => !statusParcela(p).startsWith('cancel')).length, 0);
  // Quando as cobranças mostradas foram conferidas com a CPFL pela última vez (vale o mais antigo entre elas)
  const cpflTextoSync = (() => {
    if (cpflCharges.length === 0) return '';
    const tempos = cpflCharges.map((c) => (c.ultimaSincronizacao ? new Date(c.ultimaSincronizacao).getTime() : NaN));
    if (tempos.some((t) => Number.isNaN(t))) return ' · sem atualização registrada';
    const mais = Math.min(...tempos);
    const quando = new Date(mais).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '');
    const horas = (Date.now() - mais) / 3600000;
    return ` · atualizado em ${quando}${horas > 30 ? ' (há mais de 1 dia: pode estar desatualizado)' : ''}`;
  })();

  const sortedCpflCharges = [...cpflCharges].sort((a, b) => new Date(b.dataCadastro || 0) - new Date(a.dataCadastro || 0));
  const displayedCpflCharges = showAll ? sortedCpflCharges : sortedCpflCharges.slice(0, 5);

  const isEmpty = isCpfl ? cpflCharges.length === 0 : payments.length === 0;
  const totalCount = isCpfl ? cpflCharges.length : payments.length;

  return (
    <div className="payments-overlay" onClick={onClose}>
      <div className="payments-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="payments-modal-header">
          <div className="payments-header-left">
            <FileText size={20} className="payments-icon" />
            <div>
              <h3>Cobranças do Cliente</h3>
              <p>{client.full_name || client.name}</p>
            </div>
          </div>
          <button className="payments-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="payments-modal-body">
          {loading ? (
            <div className="payments-loading">
              <div className="spinner" />
              <p>Carregando cobranças...</p>
            </div>
          ) : error ? (
            <div className="payments-error">
              <AlertCircle size={24} />
              <p>{error}</p>
            </div>
          ) : isEmpty ? (
            <div className="payments-empty">
              <FileText size={48} />
              <p>Nenhuma cobrança encontrada</p>
            </div>
          ) : isCpfl ? (
            <>
              {/* Resumo CPFL */}
              <div className="payments-summary-cards">
                <div className="summary-card">
                  <span className="summary-label">Total</span>
                  <span className="summary-value">{formatCurrency(cpflTotalValue)}</span>
                </div>
                <div className="summary-card received">
                  <span className="summary-label">Finalizadas</span>
                  <span className="summary-value">{formatCurrency(cpflFinalizadaValue)}</span>
                </div>
                <div className="summary-card pending">
                  <span className="summary-label">Aguardando / Vencidas</span>
                  <span className="summary-value">{formatCurrency(cpflAguardandoValue)}</span>
                </div>
              </div>

              {/* Tabela CPFL — uma linha por PARCELA (não por produto), pra
                  mostrar todos os meses pagos/faturados de cada cobrança */}
              <div className="payments-table-wrapper">
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Valor</th>
                      <th>Produto</th>
                      <th>Mês Ref.</th>
                      <th>Vencimento</th>
                      <th>Identificação</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedCpflCharges.flatMap((charge) => {
                      const parcelas = Array.isArray(charge.detalheParcelas) ? charge.detalheParcelas : [];

                      if (parcelas.length === 0) {
                        // Ainda não sincronizado com detalhe de parcela — mostra a
                        // linha única do produto, como antes (fallback).
                        const statusInfo = CPFL_STATUS_MAP[charge.status] || { text: 'Desconhecido', icon: AlertCircle, color: '#64748b' };
                        const StatusIcon = statusInfo.icon;
                        return [(
                          <tr key={charge.id}>
                            <td className="payment-value-cell">{formatCurrency(charge.valor)}</td>
                            <td><span className="payment-description" title={charge.produto}>{charge.produto || '—'}</span></td>
                            <td>{charge.mesReferencia || '—'}</td>
                            <td>{formatDate(charge.dataVencimento)}</td>
                            <td><span style={{ fontSize: 12, color: '#64748b' }}>{charge.identificacao}</span></td>
                            <td>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: statusInfo.color, fontWeight: 600 }}>
                                <StatusIcon size={14} /> {statusInfo.text}
                              </span>
                            </td>
                          </tr>
                        )];
                      }

                      // Uma linha por parcela — status vem como TEXTO da CPFL
                      // ("Arrecadada", "Faturada", "Ativo", etc), não número.
                      const corPorTexto = {
                        arrecadada: '#059669', arrecadado: '#059669',
                        faturada: '#d97706', faturado: '#d97706',
                        ativo: '#2563eb', ativa: '#2563eb',
                        cancelada: '#64748b', cancelado: '#64748b',
                        irregular: '#e11d48',
                        estornada: '#e11d48', estornado: '#e11d48',
                      };
                      return ordenarParcelas(parcelas).map((parcela, idx) => {
                        const textoStatus = String(parcela.status || 'Desconhecido');
                        const cor = corPorTexto[textoStatus.toLowerCase()] || '#64748b';
                        const ehPaga = textoStatus.toLowerCase().startsWith('arrecad');
                        return (
                          <tr key={`${charge.id}-${idx}`}>
                            <td className="payment-value-cell">{formatCurrency(parseFloat(parcela.valor) || charge.valor)}</td>
                            <td><span className="payment-description" title={charge.produto}>{charge.produto || '—'}</span></td>
                            <td>{parcela.mesReferencia || charge.mesReferencia || '—'}</td>
                            <td>{dataBRParcela(parcela.dataVencimento) || formatDate(charge.dataVencimento)}</td>
                            <td><span style={{ fontSize: 12, color: '#64748b' }}>{charge.identificacao}</span></td>
                            <td>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: cor, fontWeight: 600 }}>
                                {ehPaga ? <Check size={14} /> : <Clock size={14} />} {textoStatus}
                              </span>
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>

                {/* Anexos enviados no cadastro (documento + áudio de autorização) */}
                {displayedCpflCharges.some((c) => Array.isArray(c.anexos) && c.anexos.length > 0) && (
                  <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
                      Anexos do cadastro (CPFL)
                    </div>
                    {displayedCpflCharges
                      .filter((c) => Array.isArray(c.anexos) && c.anexos.length > 0)
                      .map((c) => {
                        const reais = anexosPorCharge[c.id];
                        const buscando = buscandoAnexos[c.id];
                        return (
                          <div key={c.id} style={{ marginBottom: 8 }}>
                            {reais === undefined && (
                              <button
                                onClick={() => buscarAnexosReais(c.id)}
                                disabled={buscando}
                                style={{
                                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                                  border: '1px solid #d97706', background: buscando ? '#fef3c7' : '#fffbeb',
                                  color: '#d97706', cursor: buscando ? 'default' : 'pointer', fontWeight: 600,
                                }}
                              >
                                {buscando ? 'Buscando no FTP...' : `Buscar ${c.anexos.length} anexo(s)`}
                              </button>
                            )}
                            {reais !== undefined && reais.length === 0 && (
                              <div style={{ fontSize: 13, color: '#94a3b8' }}>Nenhum arquivo encontrado no FTP.</div>
                            )}
                            {reais !== undefined && reais.map((arq, idx) => (
                              <a
                                key={idx}
                                href={`/api/cpfl/anexos/${c.id}/download?empresa=${encodeURIComponent(arq.empresa)}&nome=${encodeURIComponent(arq.nome)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: 13, color: '#2563eb', display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', textDecoration: 'none' }}
                              >
                                <FileText size={13} style={{ flexShrink: 0 }} />
                                {arq.nome} <span style={{ color: '#94a3b8' }}>({(arq.tamanho / 1024).toFixed(0)} KB)</span>
                              </a>
                            ))}
                          </div>
                        );
                      })}
                  </div>
                )}

                {cpflCharges.length > 5 && (
                  <div className="view-all-container" style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button
                      onClick={() => setShowAll(!showAll)}
                      className="view-all-btn"
                      style={{
                        padding: '8px 24px',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {showAll ? 'Ver menos' : `Ver todas as ${cpflCharges.length} cobranças`}
                    </button>
                  </div>
                )}
              </div>

              <div className="payments-footer-info">
                {cpflCharges.length <= 5
                  ? `Exibindo ${cpflCharges.length} cobrança${cpflCharges.length !== 1 ? 's' : ''}${cpflQtdParcelas > 0 ? ` · ${cpflQtdParcelas} parcela${cpflQtdParcelas !== 1 ? 's' : ''}` : ''}${cpflTextoSync}`
                  : showAll
                    ? `Exibindo todas as ${cpflCharges.length} cobranças`
                    : `Exibindo as 5 cobranças mais recentes de um total de ${cpflCharges.length}`
                }
              </div>
            </>
          ) : (
            <>
              {/* Resumo */}
              <div className="payments-summary-cards">
                <div className="summary-card">
                  <span className="summary-label">Total</span>
                  <span className="summary-value">{formatCurrency(totalValue)}</span>
                </div>
                <div className="summary-card received">
                  <span className="summary-label">Recebidas</span>
                  <span className="summary-value">{formatCurrency(receivedValue)}</span>
                </div>
                <div className="summary-card pending">
                  <span className="summary-label">Pendentes</span>
                  <span className="summary-value">{formatCurrency(pendingValue)}</span>
                </div>
              </div>

              {/* Tabela */}
              <div className="payments-table-wrapper">
                <table className="payments-table">
                  <thead>
                    <tr>
                      <th>Valor</th>
                      <th>Descrição</th>
                      <th>Forma de pagamento</th>
                      <th>Data de vencimento</th>
                      <th style={{ width: '340px' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPayments.map((payment) => {
                      const statusInfo = getStatusInfo(payment.status);
                      const StatusIcon = statusInfo.icon;
                      
                      return (
                        <tr key={payment.id}>
                          <td className="payment-value-cell">
                            {formatCurrency(payment.value)}
                          </td>
                          <td>
                            <span className="payment-description" title={payment.description}>
                              {payment.description || '—'}
                            </span>
                          </td>
                          <td>
                            <span className="billing-type-text">
                              {getBillingTypeText(payment.billingType)}
                            </span>
                          </td>
                          <td>
                            <div className="payment-due-date">
                              <span>{formatDate(payment.dueDate)}</span>
                              {payment.status === 'OVERDUE' && (
                                <AlertCircle size={14} className="overdue-icon" />
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="payment-actions-compact">
                              {/* Status - APENAS ÍCONE */}
                              <div 
                                className="status-icon-only"
                                title={statusInfo.text}
                                style={{ 
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '24px',
                                  height: '24px',
                                  marginRight: '8px'
                                }}
                              >
                                <StatusIcon size={16} style={{ color: statusInfo.color }} />
                              </div>
                              
                              {/* Botões de Ação */}
                              <div className="action-buttons-group">
                                {/* Baixar boleto */}
                                {payment.bankSlipUrl && (
                                  <a 
                                    href={payment.bankSlipUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="payment-action-icon" 
                                    title="Baixar boleto"
                                  >
                                    <DollarSign size={14} />
                                  </a>
                                )}
                                
                                {/* Ver fatura */}
                                {payment.invoiceUrl && (
                                  <a 
                                    href={payment.invoiceUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="payment-action-icon" 
                                    title="Ver fatura"
                                  >
                                    <FileText size={14} />
                                  </a>
                                )}
                                
                                {/* Confirmar pagamento */}
                                {(payment.status === 'PENDING' || payment.status === 'OVERDUE') && (
                                  <button 
                                    className="payment-action-icon" 
                                    title="Confirmar pagamento"
                                    onClick={() => handleConfirmPayment(payment)}
                                  >
                                    <Check size={14} />
                                  </button>
                                )}
                                
                                {/* Editar - APENAS se PENDING */}
                                {payment.status === 'PENDING' && (
                                  <button 
                                    className="payment-action-icon" 
                                    title="Editar cobrança"
                                    onClick={() => handleEditPayment(payment)}
                                  >
                                    <Edit size={14} />
                                  </button>
                                )}
                                
                                {/* Excluir - APENAS se PENDING */}
                                {payment.status === 'PENDING' && (
                                  <button 
                                    className="payment-action-icon delete" 
                                    title="Remover cobrança"
                                    onClick={() => handleDeletePayment(payment)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                )}
                                
                                {/* Enviar mensagem */}
                                <button 
                                  className="payment-action-icon" 
                                  title="Enviar mensagem"
                                  onClick={() => handleSendReminder(payment)}
                                >
                                  <Send size={14} />
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {/* Botão Ver Todas */}
                {payments.length > 5 && (
                  <div className="view-all-container" style={{ textAlign: 'center', marginTop: '16px' }}>
                    <button 
                      onClick={() => setShowAll(!showAll)}
                      className="view-all-btn"
                      style={{
                        padding: '8px 24px',
                        background: '#2563eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {showAll ? 'Ver menos' : `Ver todas as ${payments.length} cobranças`}
                    </button>
                  </div>
                )}
              </div>

              {/* Footer info */}
              <div className="payments-footer-info">
                {payments.length <= 5 
                  ? `Exibindo ${payments.length} cobrança${payments.length !== 1 ? 's' : ''}`
                  : showAll 
                    ? `Exibindo todas as ${payments.length} cobranças`
                    : `Exibindo as 5 cobranças mais recentes de um total de ${payments.length}`
                }
              </div>
            </>
          )}
        </div>

        {/* Footer buttons */}
        <div className="payments-modal-footer">
          <button className="btn-secondary" onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}
